import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { useT } from "../i18n/I18nContext";
import { CheckCircle2, MailX } from "lucide-react";

export default function VerifyEmail() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "loading" : "missing"); // loading | ok | error | missing
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.post("/auth/verify-email", { token })
      .then(() => { if (!cancelled) setState("ok"); })
      .catch((err) => {
        if (cancelled) return;
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        setState("error");
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md card-mabw p-8 md:p-10 anim-fade-up text-center" data-testid="verify-page">
        <div className="flex items-center gap-3 justify-center mb-2">
          <Logo size={40} />
          <span className="font-semibold" style={{ fontFamily: "Outfit" }}>MABW</span>
        </div>

        {state === "loading" && (
          <p className="mt-6 text-mabw-soft" data-testid="verify-loading">{t("common.loading")}</p>
        )}

        {state === "ok" && (
          <div data-testid="verify-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mt-6 mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("auth.verify_success_title")}
            </h1>
            <p className="text-sm text-mabw-soft mt-2">{t("auth.verify_success_desc")}</p>
            <Link to="/app" className="btn-mabw mt-6 inline-flex" data-testid="verify-enter-app">
              {t("landing.cta_enter_app")}
            </Link>
          </div>
        )}

        {(state === "error" || state === "missing") && (
          <div data-testid="verify-error">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 text-red-600 flex items-center justify-center mt-6 mb-4">
              <MailX className="w-7 h-7" />
            </div>
            <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("auth.verify_error_title")}
            </h1>
            <p className="text-sm text-mabw-soft mt-2">
              {state === "missing" ? t("auth.err_no_token") : error}
            </p>
            <Link to="/login" className="btn-mabw-secondary mt-6 inline-flex" data-testid="verify-go-login">
              {t("auth.back_to_login")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
